<template>
  <aside class="sidebar">
    <!-- 로고 영역 (고정) -->
    <div class="p-4 border-b border-slate-100 flex-shrink-0">
      <router-link to="/partners" class="block">
        <div class="flex flex-col">
          <span class="text-[17px] font-black text-slate-900 tracking-tight">물어다줄개</span>
          <div class="flex items-center gap-1.5 mt-0.5">
            <span class="text-[11px] font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 px-2 py-0.5 rounded-full tracking-wide">PARTNERS</span>
            <span class="text-[11px] font-medium text-slate-400">center</span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- 파트너 프로필 정보 -->
    <div class="p-4 border-b border-slate-100 flex-shrink-0 bg-slate-50">
      <div class="flex items-center gap-3">
        <div class="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <span class="text-white font-bold text-[15px]">{{ userInitial }}</span>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-[14px] font-bold text-slate-900 truncate">
            {{ userProfile?.name || '파트너' }}
          </p>
          <p class="text-[11px] text-slate-500 truncate">{{ userProfile?.email || '' }}</p>
        </div>
      </div>
      <!-- 현장 정보 -->
      <div v-if="!loaded" class="mt-3 p-2.5 bg-slate-100 rounded-xl border border-slate-200 animate-pulse">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 bg-slate-200 rounded-lg"></div>
          <div class="flex-1">
            <div class="h-3 bg-slate-200 rounded w-24 mb-1"></div>
            <div class="h-2 bg-slate-200 rounded w-12"></div>
          </div>
        </div>
      </div>
      <div v-else-if="currentProperty" class="mt-3 p-2.5 bg-white rounded-xl border border-slate-200">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-[12px] font-bold text-slate-900 truncate">{{ currentProperty.propertyName }}</p>
            <p class="text-[10px] text-slate-400">소속 현장</p>
          </div>
        </div>
      </div>
      <div v-else-if="loaded" class="mt-3 p-2.5 bg-amber-50 rounded-xl border border-amber-200">
        <p class="text-[11px] text-amber-700 font-medium text-center">소속 현장이 없습니다</p>
      </div>

      <!-- 포인트 잔액 -->
      <router-link
        to="/partners/point"
        class="mt-3 p-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 block hover:from-blue-100 hover:to-indigo-100 transition"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span class="text-white text-[10px] font-bold">P</span>
            </div>
            <span class="text-[11px] text-slate-500">파트너스 포인트</span>
          </div>
          <span class="text-[13px] font-bold text-blue-600">{{ pointBalance.toLocaleString() }}P</span>
        </div>
      </router-link>
    </div>

    <!-- 네비게이션 메뉴 (스크롤 영역) -->
    <nav ref="navRef" class="flex-1 p-4 overflow-y-auto scrollbar-thin">
      <!-- 대시보드 -->
      <router-link
        to="/partners"
        class="flex items-center gap-3 px-4 py-3 rounded-xl mb-2 transition-all"
        :class="$route.path === '/partners' ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
        </svg>
        <span class="text-[15px] font-bold">대시보드</span>
      </router-link>

      <!-- 고객 관리 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('customers')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isCustomersActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span class="text-[15px] font-bold">고객 관리</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.customers ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.customers" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/leads/designated"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/leads/designated' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            신규 지목 DB
          </router-link>
          <router-link
            to="/partners/leads/open"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/leads/open' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            선착순 줍기
          </router-link>
          <router-link
            to="/partners/leads/customers"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/leads/customers' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 고객 보관함
          </router-link>
          <router-link
            to="/partners/leads/banner"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/leads/banner' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 유입 콜
          </router-link>
        </div>
      </div>

      <!-- 현장 관리 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('property')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isPropertyActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
            </svg>
            <span class="text-[15px] font-bold">현장 관리</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.property ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.property" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/property/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/property/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 현장 정보
          </router-link>
          <router-link
            to="/partners/property/change"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/property/change' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            현장 변경 신청
          </router-link>
          <router-link
            to="/partners/property/history"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/property/history' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            심사 결과 확인
          </router-link>
        </div>
      </div>

      <!-- 커뮤니티 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('community')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isCommunityActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
            </svg>
            <span class="text-[15px] font-bold">커뮤니티</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.community ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.community" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/chat"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/chat' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            파트너스 톡
          </router-link>
          <router-link
            to="/partners/community"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/community' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            커뮤니티
          </router-link>
          <router-link
            to="/partners/notice"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path.startsWith('/partners/notice') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            공지사항
          </router-link>
          <router-link
            to="/partners/updates"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path.startsWith('/partners/updates') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            업데이트
          </router-link>
          <router-link
            to="/partners/feedback"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path.startsWith('/partners/feedback') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            건의/신고
          </router-link>
        </div>
      </div>

      <div class="h-px bg-slate-100 my-4"></div>

      <!-- 현장 점프 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('jump')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isJumpActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
            <span class="text-[15px] font-bold">현장 점프</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.jump ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.jump" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/jump/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/jump/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            점프 구매
          </router-link>
          <router-link
            to="/partners/jump/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/jump/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 점프 관리
          </router-link>
          <router-link
            to="/partners/jump/stats"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/jump/stats' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            점프 효과 통계
          </router-link>
        </div>
      </div>

      <!-- 캠페인 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('campaign')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isCampaignActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
            </svg>
            <span class="text-[15px] font-bold">캠페인</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.campaign ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.campaign" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/ad/create"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/ad/create' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            캠페인 만들기
          </router-link>
          <router-link
            to="/partners/ad/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/ad/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 캠페인
          </router-link>
        </div>
      </div>

      <!-- 인앱 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('adbanner')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isAdbannerActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span class="text-[15px] font-bold">인앱 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.adbanner ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.adbanner" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/adbanner/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/adbanner/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/adbanner/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/adbanner/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 인앱 광고
          </router-link>
        </div>
      </div>

      <!-- 팝업 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('popup')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isPopupActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
            </svg>
            <span class="text-[15px] font-bold">팝업 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.popup ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.popup" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/popup/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/popup/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/popup/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/popup/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 팝업 광고
          </router-link>
        </div>
      </div>

      <!-- 스플래시 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('splash')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isSplashActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-[15px] font-bold">스플래시 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.splash ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.splash" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/splash/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/splash/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/splash/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/splash/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 스플래시 광고
          </router-link>
        </div>
      </div>

      <!-- 추천 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('recommend')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isRecommendActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
            </svg>
            <span class="text-[15px] font-bold">추천 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.recommend ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.recommend" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/recommend/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/recommend/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/recommend/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/recommend/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 추천 광고
          </router-link>
        </div>
      </div>

      <!-- 리스트 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('listad')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isListadActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
            </svg>
            <span class="text-[15px] font-bold">리스트 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.listad ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.listad" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/listad/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/listad/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/listad/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/listad/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 리스트 광고
          </router-link>
        </div>
      </div>

      <!-- 꾸우챗 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('chatad')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isChatAdActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            <span class="text-[15px] font-bold">꾸우챗 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.chatad ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.chatad" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/chatad/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/chatad/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/chatad/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/chatad/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 꾸우챗 광고
          </router-link>
        </div>
      </div>

      <!-- 커뮤니티 광고 -->
      <div class="mb-2">
        <button
          @click.stop="toggleSection('communityad')"
          class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
          :class="isCommunityAdActive ? 'bg-slate-100 text-blue-600' : 'text-slate-600 hover:bg-slate-50'"
        >
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
            </svg>
            <span class="text-[15px] font-bold">커뮤니티 광고</span>
          </div>
          <svg
            class="w-4 h-4 transition-transform duration-200"
            :class="openSections.communityad ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div v-show="openSections.communityad" class="mt-2 ml-4 pl-4 border-l-2 border-slate-200 space-y-1">
          <router-link
            to="/partners/communityad/purchase"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/communityad/purchase' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            광고 신청
          </router-link>
          <router-link
            to="/partners/communityad/my"
            class="block px-4 py-2.5 rounded-lg text-[14px] font-medium transition-all"
            :class="$route.path === '/partners/communityad/my' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'"
          >
            내 커뮤니티 광고
          </router-link>
        </div>
      </div>
    </nav>

    <!-- 하단 로그아웃 버튼 (고정) -->
    <div class="p-4 border-t border-slate-100 flex-shrink-0">
      <button
        @click="handleLogout"
        class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
        </svg>
        <span class="text-[14px] font-medium">로그아웃</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { ref, computed, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/common/useAuth'
import { usePartnerProperty } from '@/composables/partners/usePartnerProperty'
import { usePoint } from '@/composables/partners/usePoint'

// 전역 사이드바 스크롤 위치 저장
let savedNavScrollTop = 0

export default {
  name: 'PartnersSideBar',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { user, userProfile, logout } = useAuth()
    const { currentProperty, loaded, fetchCurrentProperty, clearProperty } = usePartnerProperty()
    const { balance: pointBalance, loadBalance: loadPointBalance } = usePoint()
    const navRef = ref(null)

    // 섹션 열림 상태
    const openSections = reactive({
      customers: false,
      property: false,
      jump: false,
      campaign: false,
      adbanner: false,
      popup: false,
      splash: false,
      recommend: false,
      listad: false,
      chatad: false,
      communityad: false,
      community: false
    })

    // 섹션 토글
    const toggleSection = (section) => {
      openSections[section] = !openSections[section]
    }

    // 활성 상태 체크
    const isCustomersActive = computed(() => route.path.startsWith('/partners/leads'))
    const isPropertyActive = computed(() => route.path.startsWith('/partners/property'))
    const isJumpActive = computed(() => route.path.startsWith('/partners/jump'))
    const isCampaignActive = computed(() => {
      return route.path.startsWith('/partners/ad/') || route.path === '/partners/ad'
    })
    const isAdbannerActive = computed(() => route.path.startsWith('/partners/adbanner'))
    const isPopupActive = computed(() => route.path.startsWith('/partners/popup'))
    const isSplashActive = computed(() => route.path.startsWith('/partners/splash'))
    const isRecommendActive = computed(() => route.path.startsWith('/partners/recommend'))
    const isListadActive = computed(() => route.path.startsWith('/partners/listad'))
    const isChatAdActive = computed(() => route.path.startsWith('/partners/chatad'))
    const isCommunityAdActive = computed(() => route.path.startsWith('/partners/communityad'))
    const isCommunityActive = computed(() => {
      return route.path === '/partners/chat' ||
             route.path === '/partners/community' ||
             route.path.startsWith('/partners/notice') ||
             route.path.startsWith('/partners/updates') ||
             route.path.startsWith('/partners/feedback')
    })

    const userInitial = computed(() => {
      const name = userProfile.value?.name || userProfile.value?.email || ''
      return name.charAt(0).toUpperCase()
    })

    // 사이드바 스크롤 위치 저장
    const saveNavScroll = () => {
      if (navRef.value) {
        savedNavScrollTop = navRef.value.scrollTop
      }
    }

    // 사이드바 스크롤 위치 복원
    const restoreNavScroll = () => {
      if (navRef.value && savedNavScrollTop > 0) {
        navRef.value.scrollTop = savedNavScrollTop
      }
    }

    // user가 로드되면 현장 정보 + 포인트 잔액 가져오기
    watch(user, (newUser) => {
      if (newUser && !loaded.value) {
        fetchCurrentProperty(newUser.uid)
        loadPointBalance()
      }
    }, { immediate: true })

    // 초기 마운트 시 현재 경로에 맞는 섹션 열기
    onMounted(() => {
      if (isCustomersActive.value) openSections.customers = true
      if (isPropertyActive.value) openSections.property = true
      if (isJumpActive.value) openSections.jump = true
      if (isCampaignActive.value) openSections.campaign = true
      if (isAdbannerActive.value) openSections.adbanner = true
      if (isPopupActive.value) openSections.popup = true
      if (isSplashActive.value) openSections.splash = true
      if (isRecommendActive.value) openSections.recommend = true
      if (isListadActive.value) openSections.listad = true
      if (isChatAdActive.value) openSections.chatad = true
      if (isCommunityAdActive.value) openSections.communityad = true
      if (isCommunityActive.value) openSections.community = true

      // 스크롤 위치 복원
      setTimeout(() => {
        restoreNavScroll()
      }, 0)

      // 스크롤 이벤트로 위치 저장
      if (navRef.value) {
        navRef.value.addEventListener('scroll', saveNavScroll)
      }
    })

    onBeforeUnmount(() => {
      if (navRef.value) {
        navRef.value.removeEventListener('scroll', saveNavScroll)
      }
    })

    const handleLogout = async () => {
      clearProperty() // 로그아웃 시 현장 정보 초기화
      await logout()
      router.push('/partners/login')
    }

    return {
      navRef,
      userProfile,
      userInitial,
      currentProperty,
      loaded,
      pointBalance,
      openSections,
      toggleSection,
      isCustomersActive,
      isPropertyActive,
      isJumpActive,
      isCampaignActive,
      isAdbannerActive,
      isPopupActive,
      isSplashActive,
      isRecommendActive,
      isListadActive,
      isChatAdActive,
      isCommunityAdActive,
      isCommunityActive,
      handleLogout
    }
  }
}
</script>

<style scoped>
.sidebar {
  width: 256px;
  min-width: 256px;
  max-width: 256px;
  height: 100%;
  background-color: white;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}
</style>
